import { useState } from 'react';
import { Eye, EyeOff, Waypoints } from 'lucide-react';
import type { Resource } from '@itr/shared';
import { escrowOf, isNoticeIgnored, noticeKey, splitNotices } from '@itr/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { Health, bulkResultMessage, runBulk, useToast, type Selection } from './ui';

type NoticeResource = Pick<Resource, 'id' | 'health' | 'statusMessage'> & { ignoredNotices?: string[] };

/**
 * Ignoring a degraded/down notice: the resource keeps being checked, but once
 * every notice behind that result is ignored it reads `up` (the server
 * applies this on every collection — see `applyIgnoredNotices`). Saving asks
 * the server for an immediate re-check, and the realtime feed carries the new
 * state back to every page showing this resource.
 */
export function useNoticeIgnore(resource: NoticeResource, onChanged?: () => void) {
  const { can } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const ignored = resource.ignoredNotices ?? [];
  const notices = splitNotices(resource.statusMessage);

  const save = async (next: string[], done: string) => {
    setBusy(true);
    try {
      await api.patch(`/api/resources/${resource.id}`, { ignoredNotices: next });
      toast(done, 'good', 'Re-checking now — the status updates in a moment.');
      onChanged?.();
    } catch (err) {
      toast('Could not update ignored notices', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  const setIgnored = (targets: string[], ignore: boolean) => {
    const keys = new Set(targets.map(noticeKey));
    const kept = ignored.filter((i) => !keys.has(noticeKey(i)));
    return save(ignore ? [...kept, ...targets] : kept, ignore ? 'Notice ignored' : 'Notice no longer ignored');
  };

  return {
    canEdit: can('resource:write'),
    busy,
    notices: notices.map((text) => ({ text, ignored: isNoticeIgnored(text, ignored) })),
    setIgnored,
  };
}

/** Each notice on its own line, with its own ignore/unignore control. */
export function NoticeList({ resource, onChanged }: { resource: NoticeResource; onChanged?: () => void }) {
  const { canEdit, busy, notices, setIgnored } = useNoticeIgnore(resource, onChanged);
  // Only degraded/down notices can be ignored; a healthy resource's message is
  // informational unless it's there *because* its notices were ignored.
  const offerIgnore =
    canEdit && (resource.health === 'degraded' || resource.health === 'down' || notices.some((n) => n.ignored));

  return (
    <div className="notice-list">
      {notices.map((notice) => (
        <div key={notice.text} className="notice-row" data-ignored={notice.ignored ? 'true' : undefined}>
          <span className="notice-text">{notice.text}</span>
          {offerIgnore && (
            <button
              className="btn btn-sm"
              disabled={busy}
              onClick={() => setIgnored([notice.text], !notice.ignored)}
              title={
                notice.ignored
                  ? 'Count this notice again — it will mark the resource degraded while it persists'
                  : 'Ignore this notice — the resource reads up while this is its only problem'
              }
            >
              {notice.ignored ? <Eye size={12} /> : <EyeOff size={12} />}
              {notice.ignored ? 'Unignore' : 'Ignore'}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * One-click version for the resource page header, next to the status: ignore
 * every current notice of a degraded resource, or un-ignore them all once it
 * reads up only because of them.
 */
export function IgnoreStatusButton({ resource, onChanged }: { resource: NoticeResource; onChanged?: () => void }) {
  const { canEdit, busy, notices, setIgnored } = useNoticeIgnore(resource, onChanged);
  if (!canEdit || notices.length === 0) return null;

  const active = notices.filter((n) => !n.ignored).map((n) => n.text);
  const ignoredNow = notices.filter((n) => n.ignored).map((n) => n.text);

  if ((resource.health === 'degraded' || resource.health === 'down') && active.length > 0) {
    return (
      <button
        className="btn btn-sm"
        disabled={busy}
        onClick={() => setIgnored(active, true)}
        title={`Ignore ${active.length === 1 ? 'this notice' : `these ${active.length} notices`} — the resource reads up while they are its only problems:\n\n${active.join('\n')}`}
      >
        <EyeOff size={12} /> Ignore
      </button>
    );
  }
  if (ignoredNow.length > 0 && active.length === 0) {
    return (
      <button
        className="btn btn-sm"
        disabled={busy}
        onClick={() => setIgnored(ignoredNow, false)}
        title={`Stop ignoring:\n\n${ignoredNow.join('\n')}`}
      >
        <Eye size={12} /> Unignore
      </button>
    );
  }
  return null;
}

/** `Health` for a resource: the tooltip lists its notices with ignore controls. */
export function ResourceHealth({
  resource,
  label,
  onChanged,
}: {
  resource: NoticeResource;
  label?: boolean;
  onChanged?: () => void;
}) {
  return (
    <Health
      health={resource.health}
      label={label}
      reason={resource.statusMessage}
      body={<NoticeList resource={resource} onChanged={onChanged} />}
    />
  );
}

/**
 * The resource is in escrow: unreachable from the server, reached through a
 * neighbouring MikroTik instead (server: escrow.ts). Every check and tool goes
 * through it until the device answers directly again, when the server drops
 * the mark and this disappears on its own.
 */
/**
 * Marks a resource whose health is kept out of every rollup above it
 * (`ignoreHealthRollup`): it still shows its own status, but never turns its
 * parent, site or client amber or red. Set per resource in its edit form or
 * in bulk with `IgnoreBulkButtons`.
 */
export function IgnoredBadge({ resource }: { resource: Pick<Resource, 'ignoreHealthRollup'> }) {
  if (!resource.ignoreHealthRollup) return null;
  return (
    <span
      className="badge"
      title="Ignored: keeps its own status, but doesn't count toward its parent, site or client status"
      style={{ whiteSpace: 'nowrap' }}
    >
      <EyeOff size={11} /> Ignored
    </span>
  );
}

/**
 * Ignore / Unignore for a selection of resources — the bulk form of the
 * "Don't count toward parent, site or client status" checkbox on a
 * resource's edit form. Each resource keeps its own status; ignoring only
 * stops it from colouring the rollups above it.
 */
export function IgnoreBulkButtons({ selection, onChanged }: { selection: Selection; onChanged?: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const apply = async (ignore: boolean) => {
    setBusy(true);
    try {
      const result = await runBulk(selection.selectedIds, (id) => api.patch(`/api/resources/${id}`, { ignoreHealthRollup: ignore }));
      const { message, intent } = bulkResultMessage(ignore ? 'Ignored' : 'Unignored', result);
      toast(message, intent, ignore ? 'They keep their own status, but no longer count toward site or client status.' : undefined);
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button
        className="btn btn-sm"
        disabled={busy}
        onClick={() => apply(true)}
        title="Keep the selected resources from turning their parent, site or client amber or red (they keep their own status)"
      >
        <EyeOff size={12} /> Ignore
      </button>
      <button className="btn btn-sm" disabled={busy} onClick={() => apply(false)} title="Count the selected resources toward parent, site and client status again">
        <Eye size={12} /> Unignore
      </button>
    </>
  );
}

export function EscrowBadge({ resource }: { resource: Pick<Resource, 'attributes'> }) {
  const escrow = escrowOf(resource);
  if (!escrow) return null;
  const through = escrow.through?.length ? `, itself reached through ${escrow.through.map((t) => `"${t.name}" (${t.address})`).join(' then ')}` : '';
  const title =
    `Not reachable directly from this server. Since ${new Date(escrow.since).toLocaleString()} its monitoring, ping, ` +
    `SSH, web and every other tool go through MikroTik "${escrow.relayName}" (${escrow.relayAddress})${through}.` +
    (escrow.evidence.length ? `\n\nThe MikroTik proved it: ${escrow.evidence.join(', ')}.` : '') +
    '\n\nCleared automatically as soon as it answers directly again.';
  return (
    <span className="badge" data-intent="info" title={title} style={{ whiteSpace: 'nowrap' }}>
      <Waypoints size={11} /> Escrow via {escrow.relayAddress}
      {escrow.through?.length ? ` +${escrow.through.length}` : ''}
    </span>
  );
}
